import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Award, Stethoscope, Phone } from "lucide-react";
import { Reveal, Stagger, StaggerItem } from "../components/Motion";
import { doctor, serviceCategories, testimonials, certificates } from "../lib/data";

const heroImage = "/generated/portrait_hero.webp";

export default function Home() {
  return (
    <div data-testid="page-home">
      {/* HERO */}
      <section className="relative pt-28 md:pt-32 pb-20 md:pb-28 overflow-hidden">
        <div className="container-luxe grid lg:grid-cols-12 gap-10 lg:gap-16 items-end">
          <div className="lg:col-span-7 order-2 lg:order-1">
            <Reveal>
              <div className="overline mb-6">Premium Aesthetic Medicine · Київ</div>
            </Reveal>
            <Reveal delay={0.05}>
              <h1 className="font-serif text-[44px] sm:text-6xl lg:text-7xl xl:text-[88px] leading-[0.95] tracking-tight text-balance">
                Краса, що
                <br />
                <em className="not-italic text-warmGray">залишається</em>
                <br />
                природною.
              </h1>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="mt-8 text-warmGray text-lg max-w-md leading-relaxed">
                {doctor.nameUa} — лікар терапевт-косметолог. Авторський підхід
                до anti-age медицини, wellness терапії та інʼєкційної краси.
              </p>
            </Reveal>
            <Reveal delay={0.25}>
              <div className="mt-10 flex flex-col sm:flex-row gap-3">
                <Link
                  to="/booking"
                  data-testid="hero-book-cta"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-ink text-bone px-8 py-4 text-[11px] tracking-[0.28em] uppercase hover:bg-black transition"
                >
                  Онлайн запис <ArrowRight size={14} strokeWidth={1.5} />
                </Link>
                <Link
                  to="/consultations"
                  data-testid="hero-consult-cta"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-ink/20 px-8 py-4 text-[11px] tracking-[0.28em] uppercase hover:bg-ink hover:text-bone transition"
                >
                  Консультація
                </Link>
              </div>
            </Reveal>

            <Reveal delay={0.35}>
              <div className="mt-14 grid grid-cols-3 gap-6 max-w-md">
                {[
                  { k: "10+", v: "років практики" },
                  { k: "5 000+", v: "вдячних пацієнтів" },
                  { k: "50+", v: "авторських протоколів" },
                ].map((s) => (
                  <div key={s.v}>
                    <div className="font-serif text-3xl md:text-4xl">{s.k}</div>
                    <div className="overline mt-1 text-warmGray">{s.v}</div>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-5 order-1 lg:order-2">
            <Reveal>
              <div className="relative">
                <img loading="eager" decoding="async" fetchPriority="high" src={heroImage}
                  onError={(e) => (e.currentTarget.src = doctor.photos.portrait1)}
                  alt={doctor.nameUa}
                  className="w-full h-[420px] sm:h-[520px] lg:h-[640px] object-cover rounded-3xl luxe-shadow"
                />
                <div className="absolute -bottom-6 left-6 right-6 md:left-8 md:right-8 rounded-2xl glass-nav p-5 flex items-center gap-4">
                  <Award size={22} strokeWidth={1.4} className="text-ink" />
                  <div className="text-xs">
                    <div className="font-medium text-ink">Сертифікований лікар</div>
                    <div className="text-warmGray">ISO 9001 · European Academy</div>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="border-y border-line bg-sand/50 overflow-hidden">
        <div className="flex whitespace-nowrap animate-marquee py-5">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="flex items-center gap-12 px-6 font-serif text-2xl md:text-3xl text-warmGray">
              <span>Anti-age медицина</span>
              <span className="text-ink">·</span>
              <span>Інʼєкційна косметологія</span>
              <span className="text-ink">·</span>
              <span>Медична корекція ваги</span>
              <span className="text-ink">·</span>
              <span>Wellness крапельниці</span>
              <span className="text-ink">·</span>
              <span>Лазерні технології</span>
              <span className="text-ink">·</span>
              <span>Естетична доглядова косметологія</span>
              <span className="text-ink">·</span>
            </div>
          ))}
        </div>
      </section>

      {/* ABOUT TEASER */}
      <section className="py-24 md:py-32">
        <div className="container-luxe grid lg:grid-cols-12 gap-10 lg:gap-20 items-center">
          <Reveal className="lg:col-span-6">
            <img loading="lazy" decoding="async" src="/generated/consultation.webp"
              onError={(e) => (e.currentTarget.src = doctor.photos.clinicDesk)}
              alt="Консультація"
              className="w-full h-[520px] object-cover rounded-3xl"
            />
          </Reveal>
          <div className="lg:col-span-6">
            <Reveal>
              <div className="overline mb-4">Про мене</div>
            </Reveal>
            <Reveal delay={0.05}>
              <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight">
                Сучасна медицина
                <br />
                та індивідуальний
                <br />
                <em className="not-italic text-warmGray">підхід.</em>
              </h2>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="mt-8 text-warmGray leading-relaxed max-w-lg">
                Працюю на перетині косметології, anti-age медицини та wellness-терапії.
                Моя філософія — природна краса, доказова база та глибокий медичний супровід
                кожного пацієнта.
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <Link
                to="/about"
                data-testid="about-link"
                className="mt-10 inline-flex items-center gap-2 text-[11px] tracking-[0.28em] uppercase border-b border-ink pb-1 hover:gap-3 transition-all"
              >
                Дізнатися більше <ArrowRight size={14} strokeWidth={1.5} />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="py-20 md:py-28 bg-sand/40">
        <div className="container-luxe">
          <div className="flex items-end justify-between gap-6 mb-14">
            <Reveal>
              <div>
                <div className="overline mb-4">Послуги</div>
                <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight max-w-2xl">
                  Преміальні напрямки клініки
                </h2>
              </div>
            </Reveal>
            <Reveal>
              <Link
                to="/services"
                data-testid="all-services-link"
                className="hidden md:inline-flex items-center gap-2 text-[11px] tracking-[0.28em] uppercase border-b border-ink pb-1"
              >
                Усі послуги <ArrowRight size={14} strokeWidth={1.5} />
              </Link>
            </Reveal>
          </div>

          <Stagger className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {serviceCategories.map((cat) => (
              <StaggerItem key={cat.slug}>
                <Link
                  to={`/services/${cat.slug}`}
                  data-testid={`service-card-${cat.slug}`}
                  className="group block relative overflow-hidden rounded-3xl bg-bone border border-line hover:border-nudeDark transition-all duration-500"
                >
                  <div className="relative h-72 overflow-hidden">
                    <img loading="lazy" decoding="async" src={cat.cover}
                      onError={(e) => cat.fallback && (e.currentTarget.src = cat.fallback)}
                      alt={cat.title}
                      className="w-full h-full object-cover transition-transform duration-[1500ms] group-hover:scale-105"
                    />
                  </div>
                  <div className="p-7">
                    <h3 className="font-serif text-2xl md:text-3xl leading-tight tracking-tight">
                      {cat.title}
                    </h3>
                    <p className="mt-3 text-sm text-warmGray">{cat.subtitle}</p>
                    <div className="mt-6 inline-flex items-center gap-2 text-[11px] tracking-[0.25em] uppercase">
                      Детальніше <ArrowRight size={14} strokeWidth={1.5} className="transition-transform group-hover:translate-x-1" />
                    </div>
                  </div>
                </Link>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* SECONDARY VALUE GRID */}
      <section className="py-24 md:py-32">
        <div className="container-luxe grid md:grid-cols-3 gap-10 md:gap-6">
          {[
            {
              icon: Stethoscope,
              title: "Медичний підхід",
              text: "Аналізи, дефіцити, метаболізм — лікую не симптом, а причину.",
            },
            {
              icon: Sparkles,
              title: "Природна естетика",
              text: "Жодного «зробленого» обличчя — лише делікатна гармонія рис.",
            },
            {
              icon: Award,
              title: "Безперервне навчання",
              text: "Європейські сертифікати, авторські протоколи, постійні стажування.",
            },
          ].map((b) => (
            <Reveal key={b.title} className="p-8 md:p-10 rounded-3xl border border-line hover:bg-sand/60 transition-colors duration-500">
              <b.icon size={28} strokeWidth={1.2} />
              <h3 className="font-serif text-2xl mt-6">{b.title}</h3>
              <p className="text-warmGray text-sm mt-3 leading-relaxed">{b.text}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CERTIFICATES TEASER */}
      <section className="py-20 md:py-28 bg-sand/40">
        <div className="container-luxe">
          <div className="flex items-end justify-between mb-10 gap-6">
            <Reveal>
              <div>
                <div className="overline mb-3">Сертифікати</div>
                <h2 className="font-serif text-4xl md:text-5xl tracking-tight">Документи довіри</h2>
              </div>
            </Reveal>
            <Link
              to="/certificates"
              data-testid="all-certificates-link"
              className="text-[11px] tracking-[0.28em] uppercase border-b border-ink pb-1"
            >
              Усі сертифікати
            </Link>
          </div>
          <Stagger className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {certificates.slice(0, 4).map((c) => (
              <StaggerItem key={c.title}>
                <Link
                  to="/certificates"
                  data-testid={`cert-teaser-${c.title}`}
                  className="block rounded-2xl overflow-hidden border border-line bg-bone group"
                >
                  <img loading="lazy" decoding="async" src={c.src} alt={c.title} className="w-full h-56 object-cover group-hover:scale-[1.03] transition-transform duration-700" />
                </Link>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* TESTIMONIALS TEASER */}
      <section className="py-24 md:py-32">
        <div className="container-luxe">
          <Reveal>
            <div className="overline mb-4">Відгуки</div>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight max-w-3xl">
              Що говорять пацієнти
            </h2>
          </Reveal>
          <Stagger className="mt-14 grid md:grid-cols-3 gap-6">
            {testimonials.slice(0, 3).map((t) => (
              <StaggerItem key={t.name}>
                <div className="relative h-full p-8 rounded-3xl bg-sand/60 border border-line">
                  <div className="font-serif text-6xl absolute top-3 left-5 text-nudeDark leading-none">“</div>
                  <p className="pt-8 text-ink leading-relaxed">{t.quote}</p>
                  <div className="mt-8 pt-6 border-t border-line">
                    <div className="font-medium">{t.name}</div>
                    <div className="overline text-warmGray mt-1">{t.location}</div>
                  </div>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* CTA STRIP */}
      <section className="container-luxe">
        <div className="relative overflow-hidden rounded-[2rem] bg-ink text-bone px-8 py-16 md:p-20 grid md:grid-cols-2 gap-10 items-center grain">
          <div>
            <div className="overline text-bone/50 mb-5">Запис на консультацію</div>
            <h3 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              Знайдемо протокол,
              <br />
              який підкреслить
              <br />
              Вашу природну красу.
            </h3>
          </div>
          <div className="md:text-right">
            <Link
              to="/booking"
              data-testid="cta-strip-book"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-bone text-ink px-10 py-5 text-[11px] tracking-[0.28em] uppercase hover:bg-nude transition"
            >
              Записатися
            </Link>
            <div className="mt-6 text-bone/60 text-sm flex md:justify-end items-center gap-3">
              <Phone size={14} strokeWidth={1.5} />
              <a href={`tel:${doctor.phonesRaw[0]}`}>{doctor.phones[0]}</a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
